import React from "react";
import { ShotCard } from "./ShotCard";
import { XGShot, circleRadius, getHomeColor, getAwayColor } from "../utils";

export type TooltipState = {
  shot: XGShot;
  left: number;
  top: number;
  placement: "above" | "below" | "left" | "right";
};

const dotBase: React.CSSProperties = {
  position: "absolute",
  transform: "translate(-50%, -50%)",
  borderRadius: "50%",
  border: "2px solid #fff",
  cursor: "pointer",
  zIndex: 3,
};

const dotSize = (r: number): React.CSSProperties => ({
  width: `${(r * 6) / 19.2}vw`,
  height: `${(r * 6) / 19.2}vw`,
});

const dotHighlight = (active: boolean): React.CSSProperties => ({
  border: active ? "2px solid #FFD54A" : "2px solid #fff",
  boxShadow: active ? "0 0 0 2px rgba(255, 213, 74, 0.35)" : "none",
});

const isSameShot = (a?: XGShot | null, b?: XGShot | null) =>
  !!a &&
  !!b &&
  a.team === b.team &&
  Math.abs(a.x - b.x) < 1e-6 &&
  Math.abs(a.y - b.y) < 1e-6 &&
  (a.minute ?? -1) === (b.minute ?? -1) &&
  (a.sec ?? -1) === (b.sec ?? -1) &&
  (a.player || "") === (b.player || "") &&
  (a.outcome || "") === (b.outcome || "") &&
  (a.foot || "") === (b.foot || "");

type Props = {
  home: XGShot[];
  away: XGShot[];
  filt: (s: XGShot) => boolean;
  layerRef: React.RefObject<HTMLDivElement | null>;
  tooltip: TooltipState | null;
  setTooltip: (v: TooltipState | null) => void;
  openShotCard: (s: XGShot) => void;
  status: any;
};

const ShotsLayerView: React.FC<Props> = ({
  home,
  away,
  filt,
  layerRef,
  tooltip,
  setTooltip,
  openShotCard,
  status,
}) => {
  const homeColor = getHomeColor(status);
  const awayColor = getAwayColor(status);

  return (
    <div ref={layerRef} className="absolute inset-0 z-[2]">
      {tooltip && (
        <div
          onClick={() => setTooltip(null)}
          aria-hidden
          style={{ position: "absolute", inset: 0, zIndex: 3 }}
        />
      )}

      {home.filter(filt).map((s, i) => {
        const r = circleRadius(s.xg);
        return (
          <div
            key={`h${i}`}
            role="button"
            tabIndex={0}
            onClick={() => openShotCard(s)}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && openShotCard(s)
            }
            style={{
              ...dotBase,
              left: `${s.x * 100}%`,
              top: `${s.y * 100}%`,
              ...dotSize(r),
              background: homeColor,
              ...dotHighlight(isSameShot(tooltip?.shot, s)),
            }}
          />
        );
      })}

      {away.filter(filt).map((s, i) => {
        const r = circleRadius(s.xg);
        return (
          <div
            key={`a${i}`}
            role="button"
            tabIndex={0}
            onClick={() => openShotCard(s)}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && openShotCard(s)
            }
            style={{
              ...dotBase,
              left: `${s.x * 100}%`,
              top: `${s.y * 100}%`,
              ...dotSize(r),
              background: awayColor,
              ...dotHighlight(isSameShot(tooltip?.shot, s)),
            }}
          />
        );
      })}

      {tooltip && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            left: tooltip.left,
            top: tooltip.top,
            zIndex: 4,
          }}
        >
          <ShotCard shot={tooltip.shot} status={status} />
        </div>
      )}
    </div>
  );
};

export default ShotsLayerView;
